import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";

import { loadClientRecord, type ClientRecordView } from "@/lib/client-api";
import {
  ClientNotAuthenticated,
  ClientNotAvailable,
} from "@/components/client-pdf-shell";

export const Route = createFileRoute("/$tenantSlug/content/$assetId")({
  component: ContentAssetView,
});

function ContentAssetView() {
  const { tenantSlug, assetId } = Route.useParams();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["client-content", tenantSlug, assetId] as const,
    queryFn: () => loadClientRecord(tenantSlug, "content_assets", assetId),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex-1 grid place-items-center text-sm text-slate-500">
        Loading&hellip;
      </div>
    );
  }
  if (isError) {
    const err = error as (Error & { status?: number }) | undefined;
    if (err?.status === 401) {
      return <ClientNotAuthenticated tenantSlug={tenantSlug} />;
    }
    return <ClientNotAvailable tenantSlug={tenantSlug} message={err?.message} />;
  }
  if (!data) {
    return <ClientNotAvailable tenantSlug={tenantSlug} />;
  }
  return <AssetShell tenantSlug={tenantSlug} view={data} fallbackTitle="Document" />;
}

function AssetShell({
  tenantSlug,
  view,
  fallbackTitle,
}: {
  tenantSlug: string;
  view: ClientRecordView;
  fallbackTitle: string;
}) {
  const payload = view.payload as {
    mime_type?: string;
    byte_size?: number;
    is_external?: boolean;
    external_url?: string;
    bytes_url?: string;
  };
  const title = view.title || fallbackTitle;
  const href = payload.is_external ? payload.external_url : payload.bytes_url;
  const mime = String(payload.mime_type || "").toLowerCase();
  const inlineRenderable =
    !payload.is_external &&
    !!href &&
    (mime === "application/pdf" || mime.startsWith("image/"));

  return (
    <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-950">
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link
            to="/$tenantSlug/client"
            params={{ tenantSlug }}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
          >
            <ArrowLeft className="size-3" /> Back to portal
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{title}</div>
            <div className="text-xs text-slate-500 truncate">
              {payload.mime_type ?? "Document"}
              {payload.byte_size ? ` — ${formatBytes(payload.byte_size)}` : ""}
            </div>
          </div>
          {href ? (
            <a
              href={href}
              target={payload.is_external ? "_blank" : undefined}
              rel={payload.is_external ? "noopener noreferrer" : undefined}
              download={payload.is_external ? undefined : true}
              className="shrink-0 inline-flex items-center gap-1 text-xs rounded border border-slate-300 dark:border-slate-700 px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {payload.is_external ? (
                <>
                  Open link <ExternalLink className="size-3" />
                </>
              ) : (
                <>
                  <Download className="size-3" /> Download
                </>
              )}
            </a>
          ) : null}
        </div>
      </header>
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-6 w-full">
        {inlineRenderable && href ? (
          mime.startsWith("image/") ? (
            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
              <img src={href} alt={title} className="max-w-full h-auto mx-auto" />
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
              <iframe title={title} src={href} className="w-full h-[80vh]" />
            </div>
          )
        ) : (
          <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 text-sm text-slate-500">
            {href
              ? "Preview not available for this file type. Use the Download button above."
              : "No file attached to this record yet."}
          </div>
        )}
      </main>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}
